import { Dispatch, SetStateAction, useLayoutEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  competitions,
  events,
  gameList,
  markets,
  marketsEuropean,
  regions,
  sports
} from 'utils/constants/sportsbook/favorite-data-fields';
import { fireEvent } from 'utils/generic/event-bus';
import { modifyFavoritesResponse } from 'utils/sportsbook/favorites/fav-sorter';
import RidGenerator from 'utils/swarm/rid-generator';
import {
  TFavoriteDataResponse,
  TMarketCountResponse
} from 'interfaces/sportsbook';
import { executeCommand, unsubscribeAllCommand } from 'services/get-data-level';
import {
  EFavoriteActions,
  FAVORITE_HOOKS_CACHE,
  getFavoriteSubscribeWhere
} from 'newhooks/sportsbook/favorites/sharedHelpers';
import {
  setFavoriteDataLoading,
  setFavoritesMarketsCount,
  setFavoriteSportsData,
  setMarketsCounts
} from 'store/actions';
import {
  getFavoriteMatches,
  getFavoriteMatchIds
} from 'store/selectors/fav-data';

type TUseFetchFavoriteDataProps = {
  additionalMarkets?: boolean;
  setLoading?: Dispatch<SetStateAction<boolean>>;
};

const FAVORITE_MATCHES_RELATIONS = {
  sport: [...sports.sport, 'type'],
  ...competitions,
  ...gameList,
  ...regions,
  ...events
};

const MARKET_COUNT_RELATIONS = {
  market: '@count',
  game: ['id']
};

const FAVORITE_DATA_CALL_PREFIX = 'FAVORITE_DATA_';
const FAVORITE_MARKETS_COUNT_CALL_PREFIX = 'FAVORITE_MARKETS_COUNT_';

export const useFetchFavoriteData = (
  options?: TUseFetchFavoriteDataProps
): void => {
  const dispatch = useDispatch();

  const hookId = useRef(RidGenerator.gCustom());
  const favoriteMatches = useSelector(getFavoriteMatches).all;
  const favoriteMatchIds = useSelector(getFavoriteMatchIds).all;

  const { setLoading, additionalMarkets } = options || {};

  const unsubscribe = () => {
    if (
      FAVORITE_HOOKS_CACHE.subscribedHookIds.fetchData &&
      FAVORITE_HOOKS_CACHE.subscribedHookIds.fetchMarkets
    ) {
      unsubscribeAllCommand([
        FAVORITE_HOOKS_CACHE.subscribedHookIds.fetchData,
        FAVORITE_HOOKS_CACHE.subscribedHookIds.fetchMarkets
      ]);
      FAVORITE_HOOKS_CACHE.subscribedHookIds.fetchData = null;
      FAVORITE_HOOKS_CACHE.subscribedHookIds.fetchMarkets = null;
      fireEvent({ key: EFavoriteActions.dataSubscriptionUnmount });
    }
  };

  useLayoutEffect(() => {
    FAVORITE_HOOKS_CACHE.mountedHookIds.fetchData.push(hookId.current);

    return () => {
      FAVORITE_HOOKS_CACHE.mountedHookIds.fetchData.splice(
        FAVORITE_HOOKS_CACHE.mountedHookIds.fetchData.indexOf(hookId.current),
        1
      );
    };
  }, []);

  useLayoutEffect(() => {
    const isCurrentHookSubscribed =
      FAVORITE_HOOKS_CACHE.subscribedHookIds.fetchData ===
      `${FAVORITE_DATA_CALL_PREFIX}${hookId.current}`;

    const isSubscribedHookUnmounted =
      !isCurrentHookSubscribed &&
      !FAVORITE_HOOKS_CACHE.mountedHookIds.fetchData.some(
        hookId =>
          FAVORITE_HOOKS_CACHE.subscribedHookIds.fetchData !==
          `${FAVORITE_DATA_CALL_PREFIX}${hookId}`
      );

    if (
      FAVORITE_HOOKS_CACHE.subscribedHookIds.fetchData?.includes(
        hookId.current
      ) ||
      isSubscribedHookUnmounted
    ) {
      unsubscribe();
    }
  }, [favoriteMatchIds.length]);

  useLayoutEffect(() => {
    if (!FAVORITE_HOOKS_CACHE.withAdditionalMarkets && additionalMarkets) {
      unsubscribe();
    }

    return () => {
      if (!FAVORITE_HOOKS_CACHE.mountedHookIds.fetchData.length) {
        unsubscribe();
      }
    };
  }, []);

  useLayoutEffect(() => {
    if (FAVORITE_HOOKS_CACHE.subscribedHookIds.fetchData !== null) {
      return;
    }

    if (favoriteMatchIds.length) {
      if (!Object.values(favoriteMatches).length) {
        dispatch(setFavoriteDataLoading(true));
      }

      FAVORITE_HOOKS_CACHE.withAdditionalMarkets = !!additionalMarkets;
      FAVORITE_HOOKS_CACHE.subscribedHookIds.fetchData = `${FAVORITE_DATA_CALL_PREFIX}${hookId.current}`;
      FAVORITE_HOOKS_CACHE.subscribedHookIds.fetchMarkets = `${FAVORITE_MARKETS_COUNT_CALL_PREFIX}${hookId.current}`;

      const getMarketsCountPromise = new Promise<TMarketCountResponse>(
        resolve =>
          executeCommand(
            FAVORITE_HOOKS_CACHE.subscribedHookIds.fetchMarkets as string,
            MARKET_COUNT_RELATIONS,
            getFavoriteSubscribeWhere(favoriteMatchIds),
            resolve,
            true,
            (data: TMarketCountResponse) => {
              if (data.game) {
                dispatch(setFavoritesMarketsCount(data.game));
              }
            }
          )
      );

      const what = {
        ...FAVORITE_MATCHES_RELATIONS,
        ...(additionalMarkets ? marketsEuropean : markets)
      };

      const getFavoriteMatchesPromise = new Promise<TFavoriteDataResponse>(
        resolve => {
          executeCommand(
            FAVORITE_HOOKS_CACHE.subscribedHookIds.fetchData as string,
            what,
            getFavoriteSubscribeWhere(
              favoriteMatchIds,
              additionalMarkets ? 'european' : 'basic'
            ),
            resolve,
            true,
            (data: TFavoriteDataResponse) => {
              dispatch(
                setFavoriteSportsData(
                  modifyFavoritesResponse(data?.sport || {})
                )
              );
            }
          );
        }
      );

      Promise.all([getMarketsCountPromise, getFavoriteMatchesPromise])
        .then(([{ game }, { sport }]) => {
          dispatch(setMarketsCounts(game));
          dispatch(setFavoriteSportsData(modifyFavoritesResponse(sport || {})));
        })
        .finally(() => {
          setLoading?.(false);
          dispatch(setFavoriteDataLoading(false));
        });
    } else {
      setLoading?.(false);
    }
  }, [favoriteMatchIds.length]);
};
